import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import NumberFormat from 'react-number-format';
import * as Globals from "../../constants/globals";
import Typography from "@material-ui/core/Typography";
import {
  printExpandables,
  formatDecimal,
  formatDate,
} from "../../utils/ExportUtils";
import DownloadButtons from "../../components/PdfAndExcelDownloadButtons";
import {EXPORT_EXCEL_CRYPTO_PORTFOLIO_BY_PLATFORM} from '../../constants/uri'
import {post, checkHttpStatus} from "../../utils/RestUtilsWithAxios";
import * as DownloadUtils from "../../utils/downloadUtils";

const normalize = (val) => {
   const str = String(val).trim();

  const [intPart, decPart = ""] = str.split(".");

  return (
    intPart.padStart(30, "0") +
    "." +
    decPart.padEnd(30, "0")
  );
};

/**
 * desc
 * @param a
 * @param b
 * @param orderBy
 * @return {number}
 */
function desc(a, b, orderBy) {
    if(orderBy !== 'lastChangeDate'){
        if (orderBy === 'ratio') {
            if(orderBy === 'ratio')
                orderBy = 'platformRatio'
            if (b[orderBy] < a[orderBy]) {
                return -1;
            }
            if (b[orderBy] > a[orderBy]) {
                return 1;
            }
            return 0;
        } else if (orderBy === 'count' || orderBy === 'marketValue'){
            const valA = normalize(a[orderBy]);
            const valB = normalize(b[orderBy]);
            return valB.localeCompare(valA)
        }
        else {
            if(a[orderBy] == null && b[orderBy] != null)
                return 1;
            else if(a[orderBy] != null && b[orderBy] == null)
                return -1;
            else if (a[orderBy] == null && b[orderBy] == null)
                return 0;
            else
                return b[orderBy].toString().localeCompare(a[orderBy].toString());
        }
    }
    else {
        if(a[orderBy] == null && b[orderBy] != null)
            return 1;
        else if(a[orderBy] != null && b[orderBy] == null)
            return -1;
        else if (a[orderBy] == null && b[orderBy] == null)
            return 0;
        else{
            const dateIndices = [ 6, 7, 8, 9, 3, 4, 0, 1 ];
            var r = 0;
            for(var i=0; i<dateIndices.length; i++){
                r = a[orderBy].charCodeAt(dateIndices[i]) - b[orderBy].charCodeAt(dateIndices[i]);
                if(r !== 0)
                    return r;
            }
        return r;
        }
    }
}

/**
 * stableSort
 * @param array
 * @param cmp
 */
function stableSort(array, cmp) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = cmp(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map(el => el[0]);
}

/**
 * getSorting
 * @param order
 * @param orderBy
 * @return {function(*=, *=): number}
 */
function getSorting(order, orderBy) {
    return order === 'desc' ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy);
}

const headStyles = makeStyles(theme => ({
    headFont : {
        fontSize: 13,
        fontWeight : 600
    }
}));

/**
 * EnhancedTableHead
 * @param props
 * @return {*}
 * @constructor
 */
function EnhancedTableHead(props) {
    const { order, orderBy, onRequestSort } = props;
    const createSortHandler = property => event => {
        onRequestSort(event, property);
    };
    const headClasses = headStyles();

    const headRows = [
        { id: 'currencyCode', numeric: false, disablePadding: false, label: "Kripto Varlık Kodu" },
        { id: 'count', numeric: true, disablePadding: false, label: props.intl.
            formatMessage({id:"PORTFOLIO.TABLE.COUNT"}) },
        { id: 'lastChangeDate', numeric: true, disablePadding: false, label: props.intl.
            formatMessage({id:"PORTFOLIO.TABLE.LAST_CHANGE_DATE"}) },
        { id: 'marketValue', numeric: true, disablePadding: false, label: props.intl.
            formatMessage({id:"PORTFOLIO.TABLE.MARKET_VALUE"}) },
        { id: 'ratio', numeric: true, disablePadding: false, label: props.intl.
            formatMessage({id:"PORTFOLIO.TABLE.ALLOCATION"}) },
    ];

    return (
        <TableHead>
            <TableRow>
                {headRows.map(row => (
                    <TableCell
                        key={row.id}
                        style={row.style ? row.style : null}
                        align={row.numeric ? 'right' : 'left'}
                        padding={row.disablePadding ? 'none' : 'default'}
                        sortDirection={orderBy === row.id ? order : false}
                        className={headClasses.headFont}
                    >
                        <TableSortLabel
                            active={orderBy === row.id}
                            direction={order}
                            onClick={createSortHandler(row.id)}
                        >
                            {row.label}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}
EnhancedTableHead.propTypes = {
    onRequestSort: PropTypes.func.isRequired,
    order: PropTypes.string.isRequired,
    orderBy: PropTypes.string.isRequired,
};

const tableStyles = makeStyles(theme => ({
    table: {
        minWidth: 750,
    },
    tableWrapper: {
        overflowX: 'auto',
    },
    fontSize13:{
        fontSize: 13
    },
    panelHeader: {
        backgroundColor: Globals.COLOR_LIGHT_GRAY,
        color: 'white',
        borderRadius: 4,
        marginBottom: 16,
        minHeight: 36
    },
    panelIcon: {
        color: 'white'
    },
    panelText: {
        margin: "6px 0"
    },
    panelContent: {
        flexDirection: "column"
    }
}));

/**
 * PlatformTable
 * @param props
 * @return {*}
 * @constructor
 */
function PlatformTable (props) {
    const classes = tableStyles();
    const [order, setOrder] = React.useState('asc');
    const [orderBy, setOrderBy] = React.useState('currencyName');
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(5);
    const rows = props.data.rows

    /**
     * handleRequestSort
     * @param event
     * @param property
     */
    function handleRequestSort(event, property) {
        const isDesc = orderBy === property && order === 'desc';
        setOrder(isDesc ? 'asc' : 'desc');
        setOrderBy(property);
    }

    /**
     * handleChangePage
     * @param event
     * @param newPage
     */
    function handleChangePage(event, newPage) {
        setPage(newPage);
    }

    /**
     * handleChangeRowsPerPage
     * @param event
     */
    function handleChangeRowsPerPage(event) {
        setRowsPerPage(+event.target.value);
        setPage(0);
    }
    const normalizeZero = (value, maxDecimal = 8) => {
         // null, undefined veya boş string kontrolü
        if (value === null || value === undefined || value === "") return "";
        
        // Tüm değer sıfırsa -> "0"
        if (/^0+(\.0+)?$/.test(value)) return "0";

        // Ondalık kısmı ayır
        const [intPart, decimalPart] = value.split(".");

        if (!decimalPart) return intPart; // ondalık yoksa integer döndür

        // Ondalık kısmı maxDecimal kadar al
        let trimmedDecimal = decimalPart.slice(0, maxDecimal);

        // Virgülden sonra sadece sıfırlar varsa boş bırak
        if (/^0+$/.test(trimmedDecimal)) return intPart;

        // Virgülden sonra son sıfırları kırp
        trimmedDecimal = trimmedDecimal.replace(/0+$/, "");

        return trimmedDecimal ? `${intPart}.${trimmedDecimal}` : intPart;
    };
    return (
                <div>
                    <ExpansionPanel>
                        <ExpansionPanelSummary classes={{root:classes.panelHeader, content:classes.panelText,
                            expandIcon: classes.panelIcon}}
                            expandIcon={<ExpandMoreIcon />}
                        >
                            <Typography variant="h6">{props.data.id}</Typography>
                        </ExpansionPanelSummary>
                        <ExpansionPanelDetails classes={{root: classes.panelContent}}>
                            <div className={classes.tableWrapper}>
                                <Table
                                    className={classes.table}
                                    aria-labelledby="tableTitle"
                                    size={'medium'}
                                >
                                    <EnhancedTableHead
                                        order={order}
                                        orderBy={orderBy}
                                        onRequestSort={handleRequestSort}
                                        rowCount={rows.length}
                                        intl={props.intl}
                                    />
                                    <TableBody>
                                        {
                                            stableSort(rows, getSorting(order, orderBy))
                                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                                .map((row, index) => {
                                                    return (
                                                        <TableRow
                                                            hover
                                                            tabIndex={-1}
                                                            key={row.name + index}
                                                        >
                                                            <TableCell component="th" key= {'currencyCode_' + index}
                                                                       id={'currencyCode_' + index} scope="row"
                                                                       className={classes.fontSize13}> {row.currencyCode}
                                                                       </TableCell>
                                                            <TableCell align="right" key= {'count_' + index}
                                                                       id={'count_' + index} scope="row"
                                                                       className={classes.fontSize13}>
                                                                <NumberFormat
                                                                    decimalScale={8}
                                                                     isNumericString
                                                                    fixedDecimalScale={false}
                                                                    value={normalizeZero(row.count)} decimalSeparator=','
                                                                    thousandSeparator='.' displayType='text' />
                                                            </TableCell>                                                           
                                                            <TableCell align="right"  key= {'lastChangeDate_' + index}
                                                                       id={'lastChangeDate_' + index}
                                                                       className={classes.fontSize13}>
                                                                {row.lastChangeDate} </TableCell>
                                                            <TableCell align="right"  key= {'marketValue_' + index}
                                                                       id={'marketValue_' + index}
                                                                       className={classes.fontSize13}>
                                                                <NumberFormat decimalScale={2} 
                                                                              isNumericString
                                                                              fixedDecimalScale={false}
                                                                              value={normalizeZero(row.marketValue)}
                                                                              decimalSeparator=','
                                                                              thousandSeparator='.' displayType='text'
                                                                />
                                                            </TableCell>    
                                                             <TableCell align="right" key= {'ratio_' + index}
                                                                id={'ratio_' + index} scope="row"
                                                                className={classes.fontSize13}>
                                                                    <NumberFormat prefix={'%'} decimalScale={2}
                                                                    fixedDecimalScale={2} value={row.platformRatio * 100}
                                                                    decimalSeparator=',' thousandSeparator='.'
                                                                    displayType='text' />
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })
                                        }
                                    </TableBody>
                                </Table>
                            </div>
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25]}
                                component="div"
                                count={rows.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                backIconButtonText = {props.intl.formatMessage({id: "TABLE.PAGINATION.PREVIOUS"})}
                                nextIconButtonText = {props.intl.formatMessage({id: "TABLE.PAGINATION.NEXT"})}
                                labelRowsPerPage = {props.intl.formatMessage({id: "TABLE.PAGINATION.PER_PAGE"})}
                                onChangePage={handleChangePage}
                                onChangeRowsPerPage={handleChangeRowsPerPage}
                            />
                        </ExpansionPanelDetails>
                    </ExpansionPanel>        
                </div>
    )
}


const useStyles = makeStyles(theme => ({
    root: {
        width: '100%',
    },
    panelWrapper: {
        
    },
    panelHeader: {
        backgroundColor: '#0abb87',
        color: 'white',
        borderRadius: 4,
        marginBottom: 16
    },
    panelIcon: {
        color: 'white'
    },
    panelContent: {
        flexDirection: "column"
    }
}));

/**
 * PlatformBasedPortfolio
 * @param props
 * @return {*}
 * @constructor
 */
export default function PlatformBasedPortfolio (props) {
    const classes = useStyles();
    const keys = Object.keys(props.securities);
    const values = props.securities;
    const selectedDate = props.selectedDate;
    var newValues = [];
    var flag = false;

    const headRows = [
        
        { id: 'currencyName', numeric: false, disablePadding: false, label: "Kripto Varlık Adı" },
        { id: 'currencyCode', numeric: false, disablePadding: false, label: "Kripto Varlık Kodu" },
        { id: 'count', numeric: true, disablePadding: false, label: props.intl.
            formatMessage({id:"PORTFOLIO.TABLE.COUNT"}) },
        { id: 'ratio', numeric: true, disablePadding: false, label: props.intl.
            formatMessage({id:"PORTFOLIO.TABLE.ALLOCATION"}) },
        { id: 'lastChangeDate', numeric: true, disablePadding: false, label: props.intl.
            formatMessage({id:"PORTFOLIO.TABLE.LAST_CHANGE_DATE"}) },
        { id: 'marketValue', numeric: true, disablePadding: false, label: props.intl.
            formatMessage({id:"PORTFOLIO.TABLE.MARKET_VALUE"}) },
    ];

    function printPdf(printData) {
        var valAndHeader = prepareValueAndHeaderList(printData);
        printExpandables(valAndHeader.valueList,valAndHeader.headerList,headRows,
            'PLATFORM DETAYINDA BAKİYE RAPORU - ' + formatDate(selectedDate),false);
    }

    /**
     * prepareValueAndHeaderList
     * @return var valueAndHeader
     */
    function prepareValueAndHeaderList(printData) {
        var valueList = [];
        var headerList = [];
        var valueAndHeader = {};
        for(var i=0;i<printData.length;i++){
            headerList.push(printData[i].id);
            var addToList = prepareItemArray(printData[i].rows);
            valueList.push(addToList);
        }
        valueAndHeader.valueList = valueList;
        valueAndHeader.headerList = headerList;
        return valueAndHeader;
    }

    /**
     * prepareItemArray
     * @param trArray
     * @return {Array}
     */
    function prepareItemArray(trArray) {
        var setArray = [];
        for(var i=0;i<trArray.length;i++){
            var valueItem = {};
            valueItem.values = [];
            valueItem.values.push(trArray[i].currencyName);
            valueItem.values.push(trArray[i].currencyCode);
            valueItem.values.push(formatDecimal(trArray[i].count));
            valueItem.values.push('%'+(formatDecimal((trArray[i].platformRatio * 100).toFixed(2))));
            valueItem.values.push(trArray[i].lastChangeDate);
            trArray[i].marketValue==="-"?valueItem.values.push("-"):valueItem.values.push(trArray[i].marketValue);
            setArray.push(valueItem);
        }
        return setArray;
    }

    /**
     * downloadAsExcel
     * @param serviceName
     * @param fileName
     */
    function downloadAsExcel(requesyBody) {
        post(EXPORT_EXCEL_CRYPTO_PORTFOLIO_BY_PLATFORM,requesyBody)
            .then(function (response) {
                DownloadUtils.downloadFileByContentExcel(response.data, "PLATFORM DETAYINDA KRİPTO VARLIK BAKİYE RAPORU" + formatDate(selectedDate) + ".xls", Globals.CONTENT_TYPE_EXCEL);
        })
        .catch(function (error) {
            checkHttpStatus(error,props.intl);
        })
    }

    return (
        <div className={classes.root}>
          {
                values.map((val) => {
                    if(newValues.length > 0) {
                        newValues.map((newVal, index) => {
                            if(val.platformName === newVal.id){
                                newValues[index].rows.push(val);
                                flag = true;
                            }
                        })
                    }
                    if(!flag){
                        newValues.push({id: val.platformName, rows: [val]})
                    }
                    flag = false;
                })
          }
          {
            newValues.map(sec => (
                <PlatformTable intl={props.intl} data={sec} />
            ))
          }
          <DownloadButtons
            pdfOnClick={() =>  { printPdf(newValues); }
            }
            excelOnClick={() => downloadAsExcel({groupedCurrencyDtoList: newValues, selectedDate: selectedDate})
            }
          />
        </div>
    );
}